import { Check, Circle, Dot } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import { Text } from "@krea/customer-features/shared-components/text";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";

interface IStepProps {
  title: string;
  description?: string;
  link?: string | null;
  active?: boolean;
  done?: boolean;
}

interface IStepperVerticalProps {
  className?: string;
  title?: string;
  steps?: IStepProps[];
}

const getRightIcon = (step: IStepProps) => {
  if (step.done) {
    return (
      <Check
        strokeWidth={2.5}
        className={twMerge(
          "tw-relative tw-w-2.5 tw-h-2.5 tw-bg-mint-500 tw-mt-2 tw-mr-2 -tw-ml-[3.5px] tw-rounded-full tw-z-10 tw-text-white tw-ring-[1px] tw-ring-mint-500",
        )}
      />
    );
  } else if (step.active) {
    return (
      <Circle className="-tw-ml-[5px] tw-mr-2 tw-mt-1.5 tw-h-3 tw-w-3 tw-bg-white tw-text-mint-500" />
    );
  } else {
    return (
      <Dot
        strokeWidth={3}
        className="-tw-ml-0.5 tw-mr-3 tw-mt-1.5 tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-black tw-text-black"
      />
    );
  }
};

const StepperVertical = ({
  className,
  title,
  steps,
}: IStepperVerticalProps) => {
  const { t } = useTranslation();

  return (
    <div className={className}>
      {title && <h3 className="tw-mb-6 tw-text-2xl">{title}</h3>}

      {steps && (
        <ul
          className={twMerge(
            "tw-grid tw-grid-cols-1 tw-content-between tw-items-center tw-gap-6 before:tw-absolute before:tw-inset-y-2 before:tw-left-0 before:tw-block before:tw-w-[2px] before:tw-rounded-[3px] before:tw-bg-primary-150 before:tw-content-['']",
            "tw-relative tw-mb-0 tw-h-full tw-list-none tw-pl-0",
          )}
        >
          {steps.map((item, index) => (
            <li
              key={index}
              data-active={item.active}
              className={twMerge("tw-relative tw-flex tw-items-start")}
            >
              <div>{getRightIcon(item)}</div>

              <div className="tw-grow">
                <Text
                  size={item.active ? "md" : "sm"}
                  className={twMerge("tw-font-bold", {
                    "tw-text-black": item.active,
                    "tw-text-gray-700": !item.active,
                    "tw-text-gray-disabled": item.done,
                  })}
                  style={{ opacity: item.done ? 0.75 : 1 }}
                >
                  <Trans
                    t={t}
                    components={{
                      a: (
                        <Text
                          role="link"
                          tabIndex={0}
                          size={item.active ? "md" : "sm"}
                          htmlTag="span"
                          className="tw-cursor-pointer tw-font-bold tw-text-primary"
                          onClick={() => {
                            if (item.link) window.open(item.link, "_blank");
                          }}
                        />
                      ),
                    }}
                  >
                    {item.title}
                  </Trans>
                </Text>

                {item.description && item.description !== "" && (
                  <Text
                    size="sm"
                    className="tw-mt-1 tw-w-full tw-rounded tw-border tw-bg-gray tw-p-2"
                  >
                    {item.description}
                  </Text>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default StepperVertical;
